import { createClient } from '@/lib/supabase/client';
import type { Hold, HoldStatus } from '@/lib/database';
import { emptyToNull, newId, orThrow, DEFAULT_PAGE_SIZE } from './useEntityList';
import { setDiamondStatus } from './diamonds';

// DB column `hold_status` maps to UI field `status` — this file owns that mapping.
type HoldRow = Omit<Hold, 'status'> & { hold_status: HoldStatus };

function toHold(row: HoldRow): Hold {
  const { hold_status, ...rest } = row;
  return { ...rest, status: hold_status };
}

function fromHold(
  input: Omit<Hold, 'id' | 'created_at' | 'status'> & { status: HoldStatus }
): Record<string, unknown> {
  return emptyToNull(
    {
      customer_id: input.customer_id,
      customer_name: input.customer_name,
      diamond_id: input.diamond_id,
      sku: input.sku,
      shape: input.shape,
      carat: input.carat,
      color: input.color,
      clarity: input.clarity,
      price_usd: input.price_usd,
      hold_date: input.hold_date || null,
      expiry_date: input.expiry_date || null,
      hold_status: input.status,
      notes: input.notes ?? '',
    },
    ['customer_id', 'diamond_id', 'sku', 'shape', 'color', 'clarity']
  );
}

export async function listHolds(): Promise<Hold[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('holds')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('hold_date', { ascending: false });
  return (orThrow(data, error) as HoldRow[]).map(toHold);
}

export async function createHold(input: Omit<Hold, 'id' | 'created_at'>): Promise<Hold> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('holds')
    .insert({ ...fromHold(input), id: newId('hold'), created_at: new Date().toISOString() })
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  const hold = toHold(orThrow(data, error) as HoldRow);
  // Placing a hold reserves the stone.
  if (hold.diamond_id) {
    await setDiamondStatus(hold.diamond_id, 'ON_HOLD').catch(() => {});
  }
  return hold;
}

export async function updateHold(
  id: string,
  patch: Partial<Omit<Hold, 'id' | 'created_at'>>
): Promise<Hold> {
  const supabase = createClient();
  const payload: Record<string, unknown> = { ...patch };
  if (patch.status) {
    payload.hold_status = patch.status;
    delete payload.status;
  }
  const { data, error } = await supabase
    .from('holds')
    .update(payload)
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  const hold = toHold(orThrow(data, error) as HoldRow);
  // Releasing a hold puts the stone back into available inventory.
  if (hold.status === 'RELEASED' && hold.diamond_id) {
    await setDiamondStatus(hold.diamond_id, 'AVAILABLE').catch(() => {});
  }
  return hold;
}
